import { Calendar, House, Mail } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { Badge } from '@components/ui/badge';
import { useWrapRequest } from '../../components/hooks/use-wrap-request';
import { Spinner } from '../../components/spinner';
import { Api } from '../../core/api';
import { CustomFacilityDetailsDto } from '../../core/api/StudioClient';
import { I18n } from '../../core/i18n';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';

interface EmployeeDataProps {
    facility: CustomFacilityDetailsDto;
}

export const EmployeeData = observer(({ facility }: EmployeeDataProps) => {
    const { generateCorporateClient, gatewayApiUrl, generateApiConfig } =
        injectTSDI(Api);
    const { formatDate } = injectTSDI(I18n);
    const navigate = useNavigate();

    const {
        internalData: { origin, creationDate }
    } = facility;

    const corporateClient = (tenantName: string) =>
        generateCorporateClient(
            generateApiConfig(gatewayApiUrl('corporate-api'), tenantName)
        );

    const employeeData = useWrapRequest(
        async (mySportsId: string, tenant: string) => {
            const client = corporateClient(tenant);
            const result =
                await client.support.employeeSupportControllerGetPage({
                    mySportsId
                });
            if (result.data.content.length) {
                return result.data.content[0];
            }
            return undefined;
        }
    );

    React.useEffect(() => {
        if (
            facility.internalData.origin.mySportsId &&
            facility.internalData.origin.tenant
        ) {
            employeeData.request([
                facility.internalData.origin.mySportsId,
                facility.internalData.origin.tenant
            ]);
        }
    }, [facility, employeeData]);

    return (
        <div className="flex flex-wrap items-center gap-2">
            {employeeData.match({
                loading: () => <Spinner size="sm" />,
                fetched: (employee) =>
                    employee && (
                        <>
                            <Badge
                                variant="muted"
                                className="cursor-pointer"
                                onClick={() => {
                                    if (origin.tenant && employee.id) {
                                        navigate(
                                            links.tenantEmployeeDetails(
                                                origin.tenant,
                                                employee.id.toString()
                                            )
                                        );
                                    }
                                }}
                            >
                                <Mail />
                                {origin.email}
                            </Badge>
                            {origin && (
                                <Badge variant="muted">
                                    <Calendar />
                                    {formatDate(creationDate)}
                                </Badge>
                            )}
                            {origin.tenant && (
                                <Badge
                                    variant="muted"
                                    className="cursor-pointer"
                                    onClick={() => {
                                        if (origin.tenant) {
                                            navigate(
                                                links.tenantDetails(
                                                    origin.tenant
                                                )
                                            );
                                        }
                                    }}
                                >
                                    <House />
                                    {origin.tenant}
                                </Badge>
                            )}
                        </>
                    )
            })}
        </div>
    );
});
